import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Music, Sparkles, ArrowRight, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { hapticLight, hapticSuccess, hapticError } from "@/utils/haptic";

export default function SubmitOptions() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // Don't redirect, just show page without user data
        setIsLoading(false);
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();
      
      if (profile) {
        setCurrentUser(profile);
      }
    } catch (error) {
      console.error("Error loading user:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <Loader2 className="w-8 h-8 animate-spin text-[#a0a0a0]" />
      </div>
    );
  }

  const totalCredits = currentUser ? (currentUser.premium_credits || 0) + (currentUser.earned_credits || 0) : 0;

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-holly-berry text-[#d0d0d0] mb-4">
            SUBMIT YOUR MUSIC
          </h1>
          <p className="text-[#808080] text-sm md:text-base max-w-xl mx-auto">
            Choose how you want to pitch your tracks to curators
          </p>
          {currentUser && (
            <p className="text-xs text-[#606060] mt-2">
              Your balance: <span className="text-green-400">${(totalCredits / 100).toFixed(2)}</span>
            </p>
          )}
        </div>

        {/* Options Grid */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {/* Self-Service Option */}
          <Link
            to={createPageUrl("Submit")}
            onClick={() => hapticLight()}
            className="group relative neuro-base rounded-2xl p-6 md:p-8 transition-all duration-300 hover:scale-[1.02] cursor-pointer"
            style={{
              background: "linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)",
            }}
          >
            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                background: "linear-gradient(135deg, rgba(0,255,128,0.05) 0%, transparent 50%)",
              }}
            />
            
            <div className="relative z-10">
              <div className="w-16 h-16 rounded-xl neuro-pressed flex items-center justify-center mb-6 mx-auto md:mx-0">
                <Music className="w-8 h-8 text-green-400" />
              </div>

              <h2 className="text-xl md:text-2xl font-holly-berry text-[#d0d0d0] mb-3">
                PITCH YOURSELF
              </h2>

              <p className="text-[#808080] text-sm mb-6 leading-relaxed">
                Browse curated playlists and submit directly. Pay per playlist ($1-4 each). Full control over where your music goes.
              </p>

              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-xs text-[#a0a0a0]">
                  <Check className="w-4 h-4 text-green-400" />
                  Choose specific playlists
                </li>
                <li className="flex items-center gap-2 text-xs text-[#a0a0a0]">
                  <Check className="w-4 h-4 text-green-400" />
                  Pay only for what you want
                </li>
                <li className="flex items-center gap-2 text-xs text-[#a0a0a0]">
                  <Check className="w-4 h-4 text-green-400" />
                  Instant submission
                </li>
              </ul>

              <div className="flex items-center justify-between">
                <span className="text-xs text-[#606060]">Starting at $1/playlist</span>
                <ArrowRight className="w-5 h-5 text-green-400 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>

          {/* Premium Service Option */}
          <Link
            to="/premium-pitch"
            onClick={() => hapticLight()}
            className="group relative neuro-base rounded-2xl p-6 md:p-8 transition-all duration-300 hover:scale-[1.02] cursor-pointer text-left"
            style={{
              background: "linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)",
            }}
          >
            {/* Premium Badge */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold"
              style={{
                background: "linear-gradient(135deg, #9333ea, #7c3aed)",
                color: "white",
              }}
            >
              PREMIUM
            </div>

            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                background: "linear-gradient(135deg, rgba(147,51,234,0.1) 0%, transparent 50%)",
              }}
            />
            
            <div className="relative z-10">
              <div className="w-16 h-16 rounded-xl neuro-pressed flex items-center justify-center mb-6 mx-auto md:mx-0">
                <Sparkles className="w-8 h-8 text-purple-400" />
              </div>

              <h2 className="text-xl md:text-2xl font-holly-berry text-[#d0d0d0] mb-3">
                WE PITCH FOR YOU
              </h2>

              <p className="text-[#808080] text-sm mb-6 leading-relaxed">
                Let our team handle everything. We'll pitch your track to 15+ relevant playlists based on your genre and style.
              </p>

              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-xs text-[#a0a0a0]">
                  <Check className="w-4 h-4 text-purple-400" />
                  15+ playlist submissions
                </li>
                <li className="flex items-center gap-2 text-xs text-[#a0a0a0]">
                  <Check className="w-4 h-4 text-purple-400" />
                  Genre-matched curation
                </li>
                <li className="flex items-center gap-2 text-xs text-[#a0a0a0]">
                  <Check className="w-4 h-4 text-purple-400" />
                  Personal outreach to curators
                </li>
                <li className="flex items-center gap-2 text-xs text-[#a0a0a0]">
                  <Check className="w-4 h-4 text-purple-400" />
                  48-hour turnaround
                </li>
              </ul>

              <div className="flex items-center justify-between">
                <div>
                  <span className="text-2xl font-bold text-purple-400">$15</span>
                  <span className="text-xs text-[#606060] ml-2">one-time</span>
                </div>
                <ArrowRight className="w-5 h-5 text-purple-400 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        </div>

        {/* Footer Note */}
        <p className="text-center text-xs text-[#505050] mt-8">
          All submissions go to real curators. No bots, no fake playlists.
        </p>
      </div>
    </div>
  );
}
